import {
  CloseButton,
  CrudMode,
  formatPhoneNumber,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  ScreenType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Permission, PermissionAction, StudentParent } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized, normalizePhoneNumber } from "services";
import ApiService from "services/apiService";
import { studentParentPageRoute } from "./StudentParentPage";
import { parentTextingOptInPageRoute } from "./StudentParentTextingOptInPage";
import { personTextingOptinPageRoute } from "pages/students/student-texting-optin/PersonTextingOptinPage";

export const StudentParentPageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  studentParent: StudentParent;
  savedCellPhone?: string;
  screenType: ScreenType;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.studentParent.id;

  const save = (studentParent: StudentParent) => {
    const phoneChanged = normalizePhoneNumber(props.savedCellPhone) !== normalizePhoneNumber(studentParent.parent?.cellPhone);
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.studentParents
        .saveUpdate(studentParent)
        .then(() => {
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
          if (phoneChanged && studentParent.parent?.cellPhone && isAuthorized(Permission.Texting, PermissionAction.View)) {
            remindTextingOptIn(studentParent);
          }
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    } else {
      ApiService.studentParents
        .saveCreate(studentParent)
        .then((response) => {
          const id = response;
          props.reroute(id, "view");
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    }
  };

  const remindTextingOptIn = (savedStudentParent: StudentParent) => {
    ApiService.textingConversations.getTenantTextingInfo().then((tenant) => {
      if (!tenant.canInfobip) return;
      return ApiService.textingOptIn.getByParentId(savedStudentParent.parentId).then((optIn) => {
        const action = optIn.id ? PermissionAction.Update : PermissionAction.Create;
        if ((optIn.optedIn && !optIn.optOutDate) || !isAuthorized(Permission.Texting, action)) return;
        if (!window.confirm(`Cell phone number saved. Would you like to opt in the new parent number ${formatPhoneNumber(savedStudentParent.parent?.cellPhone)} for texting?`)) return;

        const parentPath = studentParentPageRoute.getPath({ id: savedStudentParent.id, crudMode: "view", screenType: props.screenType });
        const optInPath = parentTextingOptInPageRoute.getPath({ parentId: savedStudentParent.parentId, screenType: "dialog" });
        const editPath = personTextingOptinPageRoute.getPath({
          id: optIn.id || 0, personId: savedStudentParent.parentId, isParent: true, crudMode: "edit", screenType: "dialog",
        });
        navigate(`../${parentPath}/${optInPath}/${editPath}`);
      });
    }).catch(() => dispatch(setAppToast(newToast("error", "Phone number saved, but the texting opt-in reminder could not be loaded."))));
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.studentParents.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (isAuthorized(props.rootPermission, PermissionAction.Delete))
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">
          {id === 0 ? "New" : ""} {props.handle}
        </Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {id > 0 && <DataModelInformation dataModelType={DataModelEnum.StudentParent} dataModelId={id} />}

        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "view" && isAuthorized(props.rootPermission, PermissionAction.Update) && (
          <UpdateButton onClick={() => props.reroute(id, "edit")} />
        )}

        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.studentParent!)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
